/**
 * Customer-reply drafting (UC-A4).
 *
 * The interface has one job beyond producing text: making it unmistakable that **the employee owns
 * the final wording**. Three choices carry that.
 *
 * The generated text lands in an editable textarea, not a read-only panel with a copy button. A
 * read-only draft is presented as finished; an editable one is presented as a starting point, and
 * the difference shows up in whether people actually read it before sending.
 *
 * The copy action copies **what is in the box**, including edits. Copying the original would
 * quietly discard the employee's corrections at the exact moment they matter.
 *
 * There is no send button, because there is no send endpoint. That boundary is in the API and it
 * is repeated here so the interface cannot drift ahead of it.
 *
 * A refusal is rendered with its department referral rather than as an error. "No draft" is a
 * legitimate outcome when the knowledge base does not cover the question, and the employee needs
 * to know who to ask next — not that something broke.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  Button,
  Card,
  Select,
  StatusPill,
  Textarea,
  directionOfText,
  requestDraft,
  useLanguage,
  type Citation,
  type DraftRefusal,
  type DraftResponse,
  type DraftTone,
  type Language,
} from "@nab/shared";

const TONES: DraftTone[] = ["formal", "neutral", "friendly"];
const LANGUAGES: Language[] = ["ar", "en"];

export interface DraftsPageProps {
  employeeId?: string;
}

export function DraftsPage({ employeeId }: DraftsPageProps): JSX.Element {
  const { t } = useTranslation("app");
  const { language: uiLanguage } = useLanguage();

  const [request, setRequest] = useState("");
  const [language, setLanguage] = useState<Language>(uiLanguage);
  const [tone, setTone] = useState<DraftTone>("neutral");
  const [draft, setDraft] = useState<DraftResponse | null>(null);
  // Held separately from `draft` so the employee's edits survive re-renders and so the copy
  // action can copy the edited text rather than what the model produced.
  const [edited, setEdited] = useState("");
  const [refusal, setRefusal] = useState<DraftRefusal | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function generate(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = request.trim();
    if (!trimmed || busy) return;

    setBusy(true);
    setRefusal(null);
    setCopied(false);
    try {
      const result = await requestDraft(
        { request: trimmed, language, tone },
        employeeId ? { employeeId } : {},
      );
      setDraft(result);
      setEdited(result.draft);
    } catch (error) {
      setDraft(null);
      setEdited("");
      if (error instanceof ApiError && error.detail) {
        setRefusal(error.detail as DraftRefusal);
      } else {
        setRefusal({
          draft_id: "",
          message: t("drafts.failed"),
          reason: "error",
          suggested_department: null,
        });
      }
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    await navigator.clipboard.writeText(edited);
    setCopied(true);
  }

  return (
    <section className="flex flex-col gap-6" aria-labelledby="drafts-heading">
      <Card>
        <h2 id="drafts-heading" className="text-heading-md text-textPrimary">
          {t("drafts.heading")}
        </h2>
        <p className="mt-1 max-w-prose text-body-sm text-textMuted">{t("drafts.description")}</p>
      </Card>

      <Card>
        <form className="flex flex-col gap-4" onSubmit={generate}>
          <Textarea
            id="draft-request"
            label={t("drafts.requestLabel")}
            rows={4}
            maxLength={4000}
            value={request}
            dir={directionOfText(request)}
            onChange={(event) => setRequest(event.target.value)}
            placeholder={t("drafts.requestPlaceholder")}
          />

          {/* Language, tone and the submit share one row: the two choices are inputs to the action
              beside them, and separating them reads as three unrelated controls. */}
          <div className="flex flex-wrap items-end gap-3">
            <Select
              id="draft-language"
              label={t("drafts.languageLabel")}
              value={language}
              onChange={(event) => setLanguage(event.target.value as Language)}
              options={LANGUAGES.map((code) => ({
                value: code,
                label: t(`drafts.language.${code}`),
              }))}
            />
            <Select
              id="draft-tone"
              label={t("drafts.toneLabel")}
              value={tone}
              onChange={(event) => setTone(event.target.value as DraftTone)}
              options={TONES.map((option) => ({
                value: option,
                label: t(`drafts.tone.${option}`),
              }))}
            />
            <Button type="submit" variant="primary" disabled={!request.trim()} loading={busy}>
              {busy ? t("drafts.generating") : t("drafts.generate")}
            </Button>
          </div>
        </form>
      </Card>

      {refusal ? (
        // Warning tones, not danger: the knowledge base not covering a question is a normal
        // outcome, and colouring it as a failure teaches employees to distrust the whole screen.
        <Card className="border-statusWarning bg-statusWarningSubtle" role="status">
          <h2 className="text-heading-sm text-textPrimary">{t("drafts.noDraftHeading")}</h2>
          <p className="mt-1 text-body-md text-textPrimary">{refusal.message}</p>
          {refusal.suggested_department ? (
            <p className="mt-2 text-body-sm text-textMuted">
              {t("noGrounding.contactDepartment", {
                department: refusal.suggested_department,
                ns: "common",
              })}
            </p>
          ) : null}
        </Card>
      ) : null}

      {draft ? (
        <Card title={t("drafts.resultHeading")}>
          {/* Repeated next to the text, not only in the page description: this is the sentence
              that has to be true, and it belongs where the employee is looking. The accent is the
              one reserved for "read this, nothing is wrong". */}
          <p className="mb-4 rounded-sm border border-accent bg-accentSubtle px-3 py-2 text-body-sm text-accent">
            {t("drafts.ownership")}
          </p>

          <Textarea
            id="draft-text"
            label={t("drafts.editableLabel")}
            rows={12}
            value={edited}
            dir={directionOfText(edited)}
            lang={draft.language}
            onChange={(event) => {
              setEdited(event.target.value);
              setCopied(false);
            }}
          />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button type="button" variant="primary" onClick={copy}>
              {t("drafts.copy")}
            </Button>
            {copied ? (
              <span className="text-label-md text-statusSuccess" role="status">
                {t("drafts.copied")}
              </span>
            ) : null}
            {edited !== draft.draft ? (
              <span className="text-label-md text-textMuted">{t("drafts.editedNotice")}</span>
            ) : null}
          </div>

          {draft.regulatory_disclaimer ? (
            <p className="mt-4 rounded-sm border border-borderSubtle bg-surfaceSunken p-3 text-body-sm text-textMuted">
              {draft.regulatory_disclaimer}
            </p>
          ) : null}

          <DraftSources citations={draft.citations} />
        </Card>
      ) : null}
    </section>
  );
}

function DraftSources({ citations }: { citations: Citation[] }): JSX.Element | null {
  const { t } = useTranslation("app");
  if (citations.length === 0) return null;

  return (
    <section className="mt-6" aria-labelledby="draft-sources-heading">
      <h3 id="draft-sources-heading" className="text-heading-sm text-textPrimary">
        {t("drafts.sourcesHeading")}
      </h3>
      <ol className="mt-3 flex flex-col gap-2">
        {citations.map((citation) => (
          <li
            key={`${citation.index}-${citation.chunk_id}`}
            className="flex flex-wrap items-baseline gap-2 rounded-sm border border-borderSubtle p-3"
          >
            {/* A plain badge rather than a `CitationChip`: there is no source panel on this screen,
                and a button that opens nothing is worse than a label. */}
            <span className="inline-flex min-w-6 items-center justify-center rounded-sm bg-accentSubtle px-1 py-1 text-label-sm text-accent">
              {citation.index}
            </span>
            <bdi className="text-body-md text-textPrimary">{citation.document_title}</bdi>
            {citation.section ? (
              <bdi className="text-label-md text-textMuted">{citation.section}</bdi>
            ) : null}
            {citation.version ? (
              <StatusPill tone="neutral">
                {t("drafts.version", { version: citation.version })}
              </StatusPill>
            ) : null}
            {citation.authority ? (
              <span className="text-label-md text-textMuted">
                <bdi>{citation.authority}</bdi>
                {citation.reference ? <> · {citation.reference}</> : null}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}
